import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("ventaphy_token"));

  // Create API instance that updates when token changes
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        "Content-Type": "application/json",
      },
    });
    
    // Add request interceptor to always include current token
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem("ventaphy_token");
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem("ventaphy_token");
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("ventaphy_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch user on mount and when tab becomes visible (for cross-device sync)
  useEffect(() => {
    fetchUser();
    
    // Refresh user when tab becomes visible (handles cross-device subscription sync)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refresh every 30 seconds if on the persona/vent pages
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("ventaphy_token");
      if (currentToken && (window.location.pathname.includes('/persona') || window.location.pathname.includes('/profile'))) {
        fetchUser();
      }
    }, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      headers: { "Content-Type": "application/json" }
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("ventaphy_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password) => {
    const response = await axios.post(`${API}/auth/register`, { email, password }, {
      headers: { "Content-Type": "application/json" }
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("ventaphy_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("ventaphy_token");
    setToken(null);
    setUser(null);
  };

  const acceptDisclaimer = async () => {
    await api.post("/auth/accept-disclaimer", { accepted: true });
    setUser((prev) => ({ ...prev, disclaimer_accepted: true }));
  };

  const selectPersona = async (persona) => {
    await api.post("/auth/select-persona", { persona });
    setUser((prev) => ({ ...prev, selected_persona: persona }));
  };

  const refreshUser = async () => {
    const currentToken = localStorage.getItem("ventaphy_token");
    if (currentToken) {
      try {
        const response = await api.get("/auth/me");
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error("Failed to refresh user:", error);
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    acceptDisclaimer,
    selectPersona,
    refreshUser,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
